import { useAtom } from "@dbeining/react-atom";
import { Form as FormikForm, Formik } from "formik";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import type {
    ISiteOption,
    IHaulierDetail,
    HaulierCreatePayload,
    HaulierUpdatePayload,
} from "../../customTypings/haulier/Haulier";
import { sitesService } from "../../services/sites/sites.service";
import { hauliersService } from "../../services/hauliers/hauliers.service";
import { useHaulierRestoreOnCreateConflict } from "../../hooks/useHaulierRestoreOnCreateConflict";
import { createEditFormSubmitHandler, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, emptyStringToNull, hydratePostalAddressForForm, mapPostalAddressToApi, nullToEmptyString, optionalNullableString, arePayloadsEqual, editFormBreadcrumbLabel, editPageTitle, EntityLabels, ListPageTitles, SectionLabels, getUserFacingErrorMessage, isUnchangedUpdateResponse, loadingPageTitle, logErrorForDev, NO_CHANGES_TOAST_MESSAGE, showToastError, showToastSuccess, showToastUnchanged, buildRecordLifecycleConfirmProps, appRoutes } from "../../utils";
import {
    buildHaulierAdminPagedListActions,
    canMutateHauliers,
    formatHaulierToastLabel,
    getHaulierAdminActionSuccessMessage,
    type HaulierAdminConfirmKind,
} from "../HauliersListPage/haulierAdminListActions";
import { HaulierEditFormGrid } from "./HaulierEditFormGrid";

dayjs.extend(utc);

function formatHaulierMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

function toUtcIsoDateOnly(value: string): string | null {
    const t = value.trim();
    if (!t) return null;
    return dayjs.utc(t, "YYYY-MM-DD").startOf("day").toISOString();
}

function fromUtcIsoToDateInput(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).format("YYYY-MM-DD");
}

type FormValues = HaulierCreatePayload & { id?: string; licenceExpiryDate?: string };

const validationSchema = Yup.object({
    id: Yup.string().optional(),
    name: Yup.string().trim().required("Name is required.").max(255, "Name must be at most 255 characters."),
    addressLine1: optionalNullableString,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: optionalNullableString,
    carrierLicenceNumber: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() : v))
        .nullable()
        .transform((v) => v ?? "")
        .optional()
        .max(64, "Licence must be at most 64 characters."),
    licenceExpiryDate: optionalNullableString,
    isEnabled: Yup.boolean().required(),
});

type ConfirmKind = HaulierAdminConfirmKind | null;

const SITE_STORAGE_KEY = "HauliersListPage.siteId";

type HaulierEditLocationState = { siteId?: string };

const emptyForm: FormValues = {
    name: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    town: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    carrierLicenceNumber: "",
    licenceExpiryDate: "",
    isEnabled: true,
};

const HaulierEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigationSiteId = (location.state as HaulierEditLocationState | null)?.siteId ?? "";
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateHauliers(roleNames);

    const isNew = !id || id === "new";

    const [siteId, setSiteId] = useState(navigationSiteId);
    const [siteDisplayName, setSiteDisplayName] = useState("");
    const [sites, setSites] = useState<ISiteOption[]>([]);
    const showSiteField = !isNew || sites.length > 1;
    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>(emptyForm);
    const [metaEnabled, setMetaEnabled] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const restoreOnCreate = useHaulierRestoreOnCreateConflict({
        restoreDeletedWithCreateValues: (payload) => {
            if (!siteId) return Promise.reject("Site is required.");
            return hauliersService.restoreDeletedWithCreateValues(siteId, payload);
        },
        onRestored: (detail) =>
            navigate(`/hauliers/${detail.id}`, { replace: true, state: { siteId: detail.siteId } }),
    });

    const headerMenuItem = useMemo(
        () => ({ isEnabled: metaEnabled, isDeleted: metaDeleted }),
        [metaEnabled, metaDeleted]
    );

    const headerMenuActions = useMemo(
        () => buildHaulierAdminPagedListActions((kind) => setConfirmKind(kind)),
        []
    );

    const applyHaulierDetail = (haulier: IHaulierDetail) => {
        setSiteId(haulier.siteId);
        if (typeof localStorage !== "undefined") {
            localStorage.setItem(SITE_STORAGE_KEY, haulier.siteId);
        }
        setSiteDisplayName(haulier.siteName ?? "");
        setInitialValues({
            id: haulier.id,
            name: haulier.name,
            ...hydratePostalAddressForForm(haulier),
            carrierLicenceNumber: nullToEmptyString(haulier.carrierLicenceNumber).toUpperCase(),
            licenceExpiryDate: fromUtcIsoToDateInput(haulier.carrierLicenceExpiryUtc),
            isEnabled: haulier.isEnabled,
        });
        setMetaEnabled(haulier.isEnabled);
        setMetaDeleted(haulier.isDeleted);
        setCreatedAt(haulier.createdAtUtc);
        setUpdatedAt(haulier.updatedAtUtc);
        setCreatedByDisplayName(haulier.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(haulier.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(haulier.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(haulier.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(haulier.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(haulier.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(haulier);
    };

    useEffect(() => {
        if (isNew) {
            setLoading(true);
            sitesService
                .listForOrganisation()
                .then((list) => {
                    setSites(list);
                    if (list.length === 1) {
                        setSiteId(list[0].id);
                    } else if (navigationSiteId && list.some((s) => s.id === navigationSiteId)) {
                        setSiteId(navigationSiteId);
                    }
                    setInitialValues(emptyForm);
                })
                .catch((e: unknown) => {
                    logErrorForDev("HaulierEditPage.sites", e);
                    setLoadError(getUserFacingErrorMessage(e, "Could not load sites."));
                })
                .finally(() => setLoading(false));
            return;
        }
        if (!id) return;
        setLoading(true);
        setLoadError(null);
        const loadSiteId =
            navigationSiteId ||
            siteId ||
            (typeof localStorage !== "undefined" ? localStorage.getItem(SITE_STORAGE_KEY) : null) ||
            "";
        if (!loadSiteId) {
            setLoadError("Site context is missing. Open this Haulier from the Hauliers list.");
            setLoading(false);
            return;
        }
        hauliersService
            .getById(loadSiteId, id)
            .then(applyHaulierDetail)
            .catch((e: unknown) => {
                logErrorForDev("HaulierEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load haulier."));
            })
            .finally(() => setLoading(false));
    }, [id, isNew, navigationSiteId]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.haulier,
                initialValues.name || `this ${EntityLabels.haulier.toLowerCase()}`
            ),
        [confirmKind, initialValues.name]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || isNew || !kind || !siteId) return;
        const op =
            kind === "disable"
                ? hauliersService.disable(siteId, id)
                : kind === "enable"
                  ? hauliersService.enable(siteId, id)
                  : kind === "softDelete"
                    ? hauliersService.softDelete(siteId, id)
                    : hauliersService.restore(siteId, id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getHaulierAdminActionSuccessMessage(kind, initialValues.name));
            if (kind === "softDelete") {
                navigate("/hauliers", { replace: true });
                return;
            }
            return hauliersService.getById(siteId, id).then(applyHaulierDetail);
        }).catch(() => setConfirmKind(null));
    };

    const buildPayload = (values: FormValues): HaulierCreatePayload => {
        const t = (s: string) => (typeof s === "string" ? s.trim() : s);
        const address = mapPostalAddressToApi(values);
        return {
            ...address,
            name: t(values.name),
            carrierLicenceNumber: emptyStringToNull(values.carrierLicenceNumber),
            carrierLicenceExpiryUtc: toUtcIsoDateOnly(values.licenceExpiryDate ?? ""),
            isEnabled: values.isEnabled,
        };
    };

    const buildUpdatePayload = (values: FormValues): HaulierUpdatePayload => {
        const { isEnabled: _isEnabled, ...createFields } = buildPayload(values);
        return { id: values.id!, ...createFields };
    };

    const buildUpdatePayloadFromDetail = (haulier: IHaulierDetail): HaulierUpdatePayload =>
        buildUpdatePayload({
            id: haulier.id,
            name: haulier.name,
            ...hydratePostalAddressForForm(haulier),
            carrierLicenceNumber: nullToEmptyString(haulier.carrierLicenceNumber).toUpperCase(),
            licenceExpiryDate: fromUtcIsoToDateInput(haulier.carrierLicenceExpiryUtc),
            isEnabled: haulier.isEnabled,
        });

    const savedUpdatePayloadRef = useRef<HaulierUpdatePayload | null>(null);

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage Hauliers.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.haulier)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/hauliers">Back to Hauliers</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.haulier, isNew)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: SectionLabels.movementParties, to: appRoutes.transportParties },
                    { text: ListPageTitles.hauliers, to: "/hauliers" },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {editPageTitle(EntityLabels.haulier, isNew)}
                </h1>
                {isNarrowScreen && !isNew && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="Haulier-edit-actions-dropdown"
                        ariaLabel="Haulier actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            {!isNew && (
                <RecordAuditMetaBar
                    formatDate={formatHaulierMetaDate}
                    formatByLine={formatByLine}
                    createdAt={createdAt}
                    createdByDisplayName={createdByDisplayName}
                    updatedAt={updatedAt}
                    lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                    isDeleted={metaDeleted}
                    isEnabled={metaEnabled}
                    deletedAuditAtUtc={deletedAuditAtUtc}
                    deletedByDisplayName={deletedByDisplayName}
                    disabledAuditAtUtc={disabledAuditAtUtc}
                    disabledByDisplayName={disabledByDisplayName}
                />
            )}

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values) => {
                    if (metaDeleted) return;
                    if (!siteId) {
                        showToastError("Site is required.");
                        return;
                    }
                    setLiveValidationAfterSubmit(true);
                    try {
                        const base = buildPayload(values);
                        if (isNew) {
                            restoreOnCreate.setPendingRestoreForm(base);
                            try {
                                const created = await hauliersService.create(siteId, base);
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess(`${formatHaulierToastLabel(base.name)} created successfully!`);
                                navigate(`/hauliers/${created.id}`, { replace: true, state: { siteId } });
                            } catch (err: unknown) {
                                if (restoreOnCreate.handleCreateError(err)) return;
                                showToastError(
                                    typeof err === "string" ? err : DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE
                                );
                            }
                            return;
                        }
                        const updatePayload = buildUpdatePayload(values);
                        if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                            setLiveValidationAfterSubmit(false);
                            showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                            return;
                        }
                        const result = await hauliersService.update(siteId, updatePayload);
                        if (isUnchangedUpdateResponse(result)) {
                            setLiveValidationAfterSubmit(false);
                            showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                            return;
                        }
                        setLiveValidationAfterSubmit(false);
                        showToastSuccess(`${formatHaulierToastLabel(base.name)} saved successfully!`);
                        navigate("/hauliers", { replace: true });
                    } catch (err: unknown) {
                        showToastError(typeof err === "string" ? err : DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ errors, touched, isSubmitting, handleSubmit, validateForm, setTouched, values }) => (
                    <FormikForm
                        noValidate
                        onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                    >
                        {metaDeleted && (
                            <Alert variant="warning" className="mb-3">
                                This haulier is deleted. Restore it before you make any changes.
                            </Alert>
                        )}

                        <HaulierEditFormGrid
                            showSiteField={showSiteField}
                            isNew={isNew}
                            metaDeleted={metaDeleted}
                            siteId={siteId}
                            setSiteId={setSiteId}
                            siteDisplayName={siteDisplayName}
                            sites={sites}
                            liveValidationAfterSubmit={liveValidationAfterSubmit}
                            errors={errors}
                            touched={touched}
                        />

                        <EditFormSplitFooter
                            start={
                                shouldShowEditFormLifecycleActions(isNew, values.id, isNarrowScreen) ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        <EditFormLifecycleActions
                                            metaDeleted={metaDeleted}
                                            metaEnabled={metaEnabled}
                                            onAction={setConfirmKind}
                                        />
                                    </div>
                                ) : undefined
                            }
                        >
                            <EditFormSaveCancelButtons
                                isSubmitting={isSubmitting}
                                disabled={metaDeleted}
                                saveLabel={isNew ? "Create" : "Save"}
                                onCancel={() => navigate("/hauliers")}
                            />
                        </EditFormSplitFooter>
                    </FormikForm>
                )}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={restoreOnCreate.restoreModalProps.show}
                onClose={(confirmed) => void restoreOnCreate.confirmRestore(confirmed)}
                title={restoreOnCreate.restoreModalProps.title}
                body={restoreOnCreate.restoreModalProps.body}
                buttonSuccessText={restoreOnCreate.restoreModalProps.buttonSuccessText}
                buttonCancelText={restoreOnCreate.restoreModalProps.buttonCancelText}
            />
        </Container>
    );
};

export { HaulierEditPage };
